"use client";
import { useMemo, useState } from "react";
import Footer from "../ui/Footer";
import Header from "../ui/Header";
import {
  DoorClosed,
  Hotel,
  LayoutDashboard,
  ShoppingCart,
  Star,
  Tags,
  UserCog,
  UserPen,
  UserRound,
  UsersRound,
  CreditCard,
  FileText,
  Receipt,
  FileSpreadsheet,
  Coins,
  Landmark,
} from "lucide-react";
import { UserMenu } from "../ui/UserMenu";
import { cn } from "@/lib/utils";
import { useSession } from "next-auth/react";
import { validateRoutes, type SessionRoute } from "@/lib/utils/validateRoutes";

const routeIconMap = {
  DoorClosed,
  Hotel,
  LayoutDashboard,
  ShoppingCart,
  Star,
  Tags,
  UserCog,
  UserPen,
  UserRound,
  UsersRound,
  CreditCard,
  FileText,
  Receipt,
  FileSpreadsheet,
  Coins,
  Landmark,
};

export default function UserLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: session, status } = useSession();

  // Mientras NextAuth resuelve la sesión no sabemos aún el rol real, así que
  // no calculamos rutas todavía: el menú muestra su esqueleto (ver UserMenu).
  const isLoadingRoutes = status === "loading";

  const routes = useMemo(() => {
    if (isLoadingRoutes) return [];
    const validated = validateRoutes(session?.user);
    return validated.map((route: SessionRoute) => ({
      name: route.name || route.label,
      path: route.path || route.route,
      Icon: routeIconMap[route.icon as keyof typeof routeIconMap] || UserPen,
      label: route.label,
    }));
  }, [session, isLoadingRoutes]);

  return (
    <div className="min-h-screen flex flex-col !bg-gray-100">
      <Header />

      {/* Menú y contenido comparten altura, borde y sombra. En escritorio el
          menú va pegado al borde izquierdo. */}
      <div className="flex flex-1 flex-col gap-4 p-4 pt-2 md:flex-row md:pl-0 md:h-[calc(100vh-80px)] lg:flex-row lg:pl-0 lg:h-[calc(100vh-80px)] xl:flex-row xl:pl-0 xl:h-[calc(100vh-80px)] 2xl:flex-row 2xl:pl-0 2xl:h-[calc(100vh-80px)]">
        {/* Sidebar menu */}
        <div
          className={cn(
            "z-20 bg-white shadow-md rounded-lg shrink-0 transition-all duration-300 w-full md:rounded-l-none lg:rounded-l-none xl:rounded-l-none 2xl:rounded-l-none",
            isOpen
              ? "md:w-64 lg:w-64 xl:w-64 2xl:w-64"
              : "md:w-[80px] lg:w-[80px] xl:w-[80px] 2xl:w-[80px]",
          )}
        >
          <UserMenu
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            defaultRoutes={routes}
            isLoading={isLoadingRoutes}
            setOpen={setIsOpen}
          />
        </div>
        {/* Main content area */}
        <main className="flex-1 min-w-0 p-4 bg-white rounded-lg shadow-md">
          {children}
        </main>
      </div>
      <Footer />
    </div>
  );
}
